/**
 * Legal pages — Privacy Policy, Terms of Service, Refund Policy.
 * Shared LegalLayout component for consistent typography. All three pages
 * export as default + named for App.js routing.
 */
import React from "react";

const LegalLayout = ({ title, subtitle, lastUpdated, children, testid }) => (
  <div className="min-h-screen" style={{background:"var(--bg-section)"}} data-testid={testid}>
    <section className="hero-blue hero-dark-bg py-16 px-5">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:"Outfit"}}>{title}</h1>
        {subtitle && <p className="mt-3 text-blue-100/85">{subtitle}</p>}
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/15 border border-blue-500/30 text-blue-300 text-xs font-semibold tracking-wider">
          LAST UPDATED · {lastUpdated}
        </div>
      </div>
    </section>
    <section className="py-14 px-5">
      <div className="max-w-4xl mx-auto card p-8 sm:p-10 prose prose-invert max-w-none">
        <div className="text-slate-200 leading-relaxed space-y-6 text-[15px]">
          {children}
        </div>
      </div>
    </section>
  </div>
);

const H2 = ({ children, id }) => (
  <h2 id={id} className="text-2xl font-bold text-white mt-8 mb-3 pt-2 border-t border-slate-700/50" style={{fontFamily:"Outfit"}}>{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-semibold text-blue-200 mt-5 mb-2" style={{fontFamily:"Outfit"}}>{children}</h3>
);
const P = ({ children }) => <p className="text-slate-300 leading-relaxed">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 text-slate-300 marker:text-blue-400">{children}</ul>;

const CONTACT_BLOCK = (
  <div className="mt-6 p-5 rounded-xl bg-blue-950/40 border border-blue-500/25">
    <div className="text-white font-semibold mb-2" style={{fontFamily:"Outfit"}}>Get in touch</div>
    <p className="text-sm text-slate-300">
      Optivize — DBA of Surajit Majhi HUF<br/>
      Email: <a href="mailto:sales@optivize.digital" className="text-blue-300 hover:underline">sales@optivize.digital</a><br/>
      Phone: +91 81006 71006<br/>
      Web: <a href="https://optivize.digital" className="text-blue-300 hover:underline">optivize.digital</a>
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// PRIVACY POLICY
// ─────────────────────────────────────────────────────────────────────────────
export function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How we collect, use, and protect your information."
      lastUpdated="23 June 2026"
      testid="privacy-policy-page"
    >
      <P>This Privacy Policy (&ldquo;Policy&rdquo;) describes how <strong className="text-white">Optivize</strong>, a brand operated by <strong className="text-white">Surajit Majhi HUF</strong> (&ldquo;Optivize&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;), collects, uses, discloses, retains, and safeguards information when you visit <a className="text-blue-300 hover:underline" href="https://optivize.digital">optivize.digital</a>, use any of our products or services (including the AI Website Builder, AI Image Studio, SEO Suite, Workflow Automation, Domain &amp; Hosting Reseller services, and Support Ticketing System), or otherwise interact with us. By accessing the Services you consent to the practices described here. If you do not agree, please discontinue use.</P>

      <H2 id="1">1. Information We Collect</H2>
      <H3>1.1 Information you provide directly</H3>
      <UL>
        <li><strong>Account data:</strong> name, email address, phone number, billing address, company name, password (stored as a bcrypt hash &mdash; we never store plaintext passwords).</li>
        <li><strong>Customer content:</strong> websites, copy, images, prompts, blog drafts, and any other content you create through our AI tools.</li>
        <li><strong>Payment metadata:</strong> we do not store full card numbers. Razorpay processes payments and we retain only the order ID, transaction ID, RRN, payment method, currency, amount and status for compliance/accounting.</li>
        <li><strong>Communications:</strong> support tickets, contact-form submissions, chat messages, and the contents of emails you send us.</li>
      </UL>
      <H3>1.2 Information collected automatically</H3>
      <UL>
        <li>IP address, browser type and version, device identifiers, time zone setting, operating system.</li>
        <li>Pages visited, links clicked, referring URL, search terms, session duration.</li>
        <li>Cookies and similar technologies (essential, preference, analytics &mdash; see Section 6).</li>
      </UL>
      <H3>1.3 Information from third parties</H3>
      <UL>
        <li>Payment processors (Razorpay) share transaction confirmation, payer name, masked card last-four.</li>
        <li>OAuth/Social-login providers (Google, Facebook) share basic profile information you authorise.</li>
        <li>Domain &amp; hosting registrars share WHOIS, DNS, hosting status and renewal data when you purchase domain services through us.</li>
      </UL>

      <H2 id="2">2. How We Use Your Information</H2>
      <UL>
        <li>To provide, operate, maintain and improve the Services.</li>
        <li>To process orders, generate invoices/receipts (PDF), apply credits, and prevent fraud.</li>
        <li>To deliver AI-generated content (text, images, SEO reports) you have requested.</li>
        <li>To send transactional emails (order confirmation, invoice, payment receipt, ticket replies, password reset).</li>
        <li>To send service announcements (only essential ones; marketing emails are opt-in).</li>
        <li>To monitor usage, detect abuse, secure our systems and comply with legal obligations.</li>
        <li>To perform aggregated analytics for product improvement (these never identify individual users).</li>
      </UL>

      <H2 id="3">3. Legal Bases (GDPR, where applicable)</H2>
      <P>If you are in the European Economic Area, the United Kingdom or any jurisdiction with a comparable framework, we process personal data on one or more of the following legal bases: <strong className="text-white">contractual necessity</strong> (to provide what you purchased), <strong className="text-white">legitimate interests</strong> (to keep the platform secure and improve it), <strong className="text-white">consent</strong> (marketing emails, optional cookies), and <strong className="text-white">legal obligation</strong> (tax/accounting retention).</P>

      <H2 id="4">4. Sharing &amp; Disclosure</H2>
      <P>We do <strong className="text-white">not sell</strong> personal data. We share data only with:</P>
      <UL>
        <li><strong>Service providers</strong> strictly bound by confidentiality &mdash; Razorpay (payments), Hostinger (SMTP), Cloudflare R2 (file storage), MongoDB Atlas (database), OpenAI / Anthropic / Google Gemini (AI generation &mdash; only the prompt you submit), ReportLab (PDF rendering, on-prem).</li>
        <li><strong>Domain &amp; hosting registrars</strong> when you purchase reseller services through us.</li>
        <li><strong>Law-enforcement</strong> only in response to a valid subpoena, court order, or to defend legal rights.</li>
        <li><strong>Successor entities</strong> in the event of a corporate restructuring, merger, acquisition or asset sale &mdash; subject to equivalent protections.</li>
      </UL>

      <H2 id="5">5. Data Retention</H2>
      <UL>
        <li>Account data: retained while your account is active and for up to <strong>3 years</strong> after closure for record-keeping.</li>
        <li>Invoices, receipts and refund records: retained for <strong>8 years</strong> as required by Indian income-tax and GST law.</li>
        <li>Support tickets: retained for <strong>3 years</strong> after closure.</li>
        <li>Marketing-email preference (unsubscribe) records: retained indefinitely to honour your choice.</li>
        <li>Backups: rotated on a 90-day cycle; deleted records may persist briefly in backups before purge.</li>
      </UL>

      <H2 id="6">6. Cookies</H2>
      <P>We use three categories of cookies:</P>
      <UL>
        <li><strong>Essential</strong> &mdash; login session, CSRF token, cart contents. Cannot be disabled without breaking the Service.</li>
        <li><strong>Preference</strong> &mdash; theme, language, currency, saved filters. Stored only in your browser.</li>
        <li><strong>Analytics</strong> &mdash; aggregate page-view counts. We do not deploy Google Analytics or any third-party advertising tracker by default; first-party analytics only.</li>
      </UL>

      <H2 id="7">7. Your Rights</H2>
      <P>Subject to local law, you may exercise the following rights free of charge by writing to <a className="text-blue-300 hover:underline" href="mailto:sales@optivize.digital">sales@optivize.digital</a>:</P>
      <UL>
        <li>Right of <strong>access</strong> &mdash; obtain a copy of the personal data we hold about you.</li>
        <li>Right to <strong>rectification</strong> &mdash; correct inaccurate or incomplete data.</li>
        <li>Right to <strong>erasure</strong> (&ldquo;right to be forgotten&rdquo;) &mdash; subject to legal retention exceptions.</li>
        <li>Right to <strong>data portability</strong> &mdash; receive your data in a structured, machine-readable format (JSON).</li>
        <li>Right to <strong>object</strong> to processing for direct marketing &mdash; we will honour immediately.</li>
        <li>Right to <strong>withdraw consent</strong> &mdash; for any consent-based processing, at any time.</li>
        <li>Right to <strong>lodge a complaint</strong> with a supervisory authority (e.g. the Indian Data Protection Board or your local EU DPA).</li>
      </UL>
      <P>We respond to verified requests within 30 days.</P>

      <H2 id="8">8. International Transfers</H2>
      <P>Our primary servers are hosted in India. Some sub-processors (e.g. OpenAI, MongoDB Atlas) operate globally. Where data is transferred outside India or the EEA we rely on Standard Contractual Clauses or equivalent safeguards.</P>

      <H2 id="9">9. Security</H2>
      <P>We use TLS 1.2+ for all transit, bcrypt for password hashing, HTTP-only secure cookies for sessions, parameterised database queries to prevent injection, scheduled vulnerability scans, and least-privilege access controls on all production systems. No method of transmission is 100% secure; we accept no liability for events outside our reasonable control.</P>

      <H2 id="10">10. Children</H2>
      <P>The Services are not directed to children under 13. We do not knowingly collect data from children. If you believe a child has provided data, please contact us and we will delete it.</P>

      <H2 id="11">11. Changes to this Policy</H2>
      <P>We may update this Policy from time to time. Material changes will be notified via email to all active customers at least 7 days before they take effect, and the &ldquo;Last updated&rdquo; date above will be revised.</P>

      <H2 id="12">12. Contact</H2>
      {CONTACT_BLOCK}
    </LegalLayout>
  );
}
export default PrivacyPolicy;

// ─────────────────────────────────────────────────────────────────────────────
// TERMS OF SERVICE
// ─────────────────────────────────────────────────────────────────────────────
export function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The agreement that governs your use of Optivize."
      lastUpdated="23 June 2026"
      testid="terms-of-service-page"
    >
      <P>These Terms of Service (&ldquo;Terms&rdquo;) form a binding agreement between you (&ldquo;Customer&rdquo;, &ldquo;you&rdquo;) and <strong className="text-white">Optivize</strong>, a brand operated by <strong className="text-white">Surajit Majhi HUF</strong> (&ldquo;Optivize&rdquo;, &ldquo;we&rdquo;). By creating an account, purchasing a service, or otherwise using <a className="text-blue-300 hover:underline" href="https://optivize.digital">optivize.digital</a>, you agree to be bound by these Terms.</P>

      <H2 id="1">1. Services Offered</H2>
      <UL>
        <li>Website design and development, including AI-assisted builders.</li>
        <li>Digital marketing, SEO audits, content writing, rank tracking, backlink analysis.</li>
        <li>AI image, video and content generation via integrated providers.</li>
        <li>Domain registration, hosting and email reseller services.</li>
        <li>API integrations, workflow automation, custom development.</li>
        <li>Support tickets and one-to-one consulting hours.</li>
      </UL>

      <H2 id="2">2. Account &amp; Eligibility</H2>
      <UL>
        <li>You must be at least 18 years old and have full legal capacity to contract.</li>
        <li>You are responsible for keeping your credentials confidential and for all activity under your account.</li>
        <li>You agree to provide accurate, current and complete information and to update it.</li>
        <li>One person/legal entity per account. Sharing is not permitted without our written consent.</li>
      </UL>

      <H2 id="3">3. Orders &amp; Payment</H2>
      <UL>
        <li>Prices are displayed in INR and may be converted to USD/EUR/GBP/AED/SGD/CAD/AUD at checkout using mid-market rates plus a transparent FX margin.</li>
        <li>Indian customers are charged GST at 18% (or other applicable rate) on top of the listed price.</li>
        <li>Payment is due immediately on order placement unless explicit credit terms are agreed in writing. Razorpay handles all primary collection.</li>
        <li>Manual / offline payment methods (UPI, bank transfer, cheque, Paytm, POS, YesPay Biz, AU QR Merchant, Bijlipay, mSwipe Pay-by-Link) are reconciled by Optivize within one business day of receipt confirmation.</li>
        <li>Invoices are issued electronically as PDF and constitute a valid tax invoice under the GST Act, 2017.</li>
      </UL>

      <H2 id="4">4. Delivery &amp; Acceptance</H2>
      <UL>
        <li>Estimated delivery timelines are stated on each product page or in the proposal; they begin only after we receive all required inputs (content, images, brand guidelines, access credentials).</li>
        <li>You are deemed to have accepted the deliverable if no written objection is received within <strong>5 business days</strong> of delivery; objections must specify the alleged defect with reasonable particularity.</li>
        <li>Two rounds of revision are included for design services unless the proposal explicitly states otherwise. Additional revisions are billable at our published hourly rate.</li>
      </UL>

      <H2 id="5">5. Customer Obligations</H2>
      <UL>
        <li>Provide all content and access credentials in good time.</li>
        <li>Ensure that all content you upload is lawful and that you own the rights or have a licence to use it.</li>
        <li>Refrain from uploading malware, spam, hate speech, sexually explicit content involving minors, or any material that infringes third-party rights.</li>
        <li>Comply with the acceptable-use policy of any third-party platform (Razorpay, Google, Facebook, hosting providers) that the deliverable interacts with.</li>
      </UL>

      <H2 id="6">6. Intellectual Property</H2>
      <UL>
        <li><strong>Our IP:</strong> the Optivize platform, source code, AI prompts library, branding assets, and any pre-existing modules remain our property.</li>
        <li><strong>Your IP:</strong> upon full payment of the invoice, you receive a worldwide, perpetual, non-exclusive licence to the deliverables we create for you, sufficient for your business operations.</li>
        <li><strong>AI-generated content:</strong> ownership is governed by the policy of the underlying provider (OpenAI, Google, Anthropic, fal.ai). Optivize transfers whatever rights it lawfully holds.</li>
        <li>You grant us a non-exclusive licence to display the deliverable in our portfolio unless you opt out in writing.</li>
      </UL>

      <H2 id="7">7. AI &amp; Automation Caveats</H2>
      <P>AI-generated text, images, code and SEO recommendations are produced by stochastic models and may contain errors, hallucinations or biases. You agree to review every AI output before relying on it for business-critical decisions. Optivize is not liable for any loss arising from blind reliance on AI output.</P>

      <H2 id="8">8. Service Levels &amp; Support</H2>
      <UL>
        <li>Standard support response within 1 business day (Monday&ndash;Saturday, 10:00&ndash;19:00 IST).</li>
        <li>Critical-incident response within 4 hours for paid &ldquo;Priority Support&rdquo; subscribers.</li>
        <li>Planned maintenance windows are announced at least 48 hours in advance via email.</li>
        <li>We target 99.5% monthly uptime; downtime caused by upstream providers or force-majeure events is excluded.</li>
      </UL>

      <H2 id="9">9. Suspension &amp; Termination</H2>
      <P>We may suspend or terminate your account, without notice, if you (a) breach these Terms, (b) attempt fraudulent payment, chargebacks or any conduct flagged by our fraud-cascade engine, or (c) put our platform or other customers at risk. You may close your account at any time by writing to support. Already-rendered services remain billable.</P>

      <H2 id="10">10. Limitation of Liability</H2>
      <P>To the maximum extent permitted by applicable law, Optivize&rsquo;s aggregate liability arising out of or in connection with the Services shall not exceed the total fees paid by you to Optivize in the <strong>12 months</strong> preceding the event giving rise to the claim. In no event shall we be liable for indirect, consequential, incidental, special, exemplary or punitive damages, lost profits, lost data, or business interruption.</P>

      <H2 id="11">11. Indemnification</H2>
      <P>You agree to indemnify and hold Optivize harmless from any claim, damage or expense (including reasonable legal fees) arising out of (a) your breach of these Terms, (b) content you upload that infringes any third-party right, or (c) your unlawful use of the Services.</P>

      <H2 id="12">12. Confidentiality</H2>
      <P>Each party will protect the other&rsquo;s Confidential Information (defined as non-public information disclosed in connection with the Services) with the same care it uses for its own information, and in no event less than reasonable care. The obligation survives termination for 3 years.</P>

      <H2 id="13">13. Force Majeure</H2>
      <P>Neither party is liable for failure or delay caused by acts of God, war, terrorism, riot, embargo, government action, pandemic, fire, flood, power failure, ISP outage, or any other event beyond reasonable control.</P>

      <H2 id="14">14. Governing Law &amp; Jurisdiction</H2>
      <P>These Terms are governed by the laws of India. Subject to the dispute-resolution clause below, the courts at <strong className="text-white">Howrah, West Bengal</strong> have exclusive jurisdiction.</P>

      <H2 id="15">15. Dispute Resolution</H2>
      <P>Before commencing litigation, the parties shall attempt to resolve any dispute in good faith for 30 days. Failing that, the dispute shall be referred to arbitration by a sole arbitrator under the Arbitration &amp; Conciliation Act, 1996, seated in Howrah. Each party bears its own costs unless the arbitrator orders otherwise.</P>

      <H2 id="16">16. Changes to these Terms</H2>
      <P>We may modify these Terms by posting an updated version with a new &ldquo;Last updated&rdquo; date. Continued use after the effective date constitutes acceptance. Material changes will be notified by email at least 14 days before they take effect.</P>

      <H2 id="17">17. Contact</H2>
      {CONTACT_BLOCK}
    </LegalLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REFUND POLICY
// ─────────────────────────────────────────────────────────────────────────────
export function RefundPolicy() {
  return (
    <LegalLayout
      title="Refund Policy"
      subtitle="Our commitment to fair refunds and the conditions under which they apply."
      lastUpdated="23 June 2026"
      testid="refund-policy-page"
    >
      <P>Optivize (a brand of <strong className="text-white">Surajit Majhi HUF</strong>) strives to deliver every project to the satisfaction of the customer. This Refund Policy explains the circumstances under which we will issue a refund, how requests are evaluated, and the timelines involved. By placing an order you accept this policy.</P>

      <H2 id="1">1. Scope</H2>
      <P>This policy applies to all paid services purchased through optivize.digital, including but not limited to website design and development, AI builders, SEO &amp; digital-marketing packages, AI image/video generation, domain &amp; hosting reseller services, AI credit top-ups, and support/consulting hours.</P>

      <H2 id="2">2. Eligibility for Refund</H2>
      <P>A refund may be granted in the following circumstances:</P>
      <UL>
        <li>The service has not been started and you cancel within <strong>24 hours</strong> of order placement. <em>Full refund.</em></li>
        <li>The service was started but materially failed to match the agreed scope and we are unable to remediate within a reasonable period. <em>Pro-rata refund.</em></li>
        <li>Duplicate payment processed in error (rare; we generally detect and refund automatically within 48 hours).</li>
        <li>Statutory rights conferred by Indian consumer-protection law that cannot be contractually waived.</li>
      </UL>

      <H2 id="3">3. Non-Refundable Items</H2>
      <P>The following are <strong>not refundable</strong> once paid:</P>
      <UL>
        <li>AI credits already consumed (text, image, video generations are billed per call to the upstream provider).</li>
        <li>Domain registrations and renewals &mdash; registrars do not refund these to Optivize.</li>
        <li>Third-party SaaS subscriptions purchased on your behalf (e.g. premium themes, stock-image packs, paid plugins).</li>
        <li>Custom development hours that have already been worked and logged.</li>
        <li>Setup fees once provisioning has begun on hosting accounts, email servers, or DNS configurations.</li>
        <li>Payment-gateway fees (Razorpay charges typically 2&ndash;3% which we cannot recover).</li>
      </UL>

      <H2 id="4">4. How to Request a Refund</H2>
      <P>Email <a className="text-blue-300 hover:underline" href="mailto:sales@optivize.digital">sales@optivize.digital</a> with the subject line &ldquo;Refund Request &mdash; [Invoice number]&rdquo; and include:</P>
      <UL>
        <li>Your account email and full invoice number.</li>
        <li>Date of payment and transaction reference (RRN / UPI ID / payment-link ID).</li>
        <li>Reason for the request and supporting evidence (screenshots, emails, etc.).</li>
        <li>The bank account or original payment method you would like the refund credited to.</li>
      </UL>

      <H2 id="5">5. Evaluation Process</H2>
      <UL>
        <li>We acknowledge receipt of every request within <strong>1 business day</strong>.</li>
        <li>The project manager assigned to your order will review the request, the work delivered, and the contractual scope.</li>
        <li>If the request is approved, we issue a refund instruction to Razorpay (or to our finance team for offline-paid invoices) within <strong>3 business days</strong>.</li>
        <li>If the request is denied, you will receive a written explanation citing the specific clause of this policy or the Terms of Service that applies.</li>
      </UL>

      <H2 id="6">6. Refund Timelines</H2>
      <UL>
        <li>Refunds to credit/debit cards: 5&ndash;10 business days (depending on issuing bank).</li>
        <li>Refunds to UPI / netbanking: 2&ndash;5 business days.</li>
        <li>Refunds via bank transfer (NEFT/RTGS) for offline-paid invoices: 2&ndash;3 business days after we receive your bank details.</li>
        <li>Refunds for international payments may take longer; intermediary-bank FX fees may apply and are deducted from the refund amount.</li>
      </UL>

      <H2 id="7">7. Cancelled or Fraud-Flagged Orders &mdash; Cascade Behaviour</H2>
      <P>When an order is moved to status &ldquo;cancelled&rdquo; or &ldquo;fraud&rdquo;:</P>
      <UL>
        <li>The associated invoice status is updated automatically (&ldquo;refunded&rdquo; or &ldquo;fraud&rdquo;).</li>
        <li>Any AI credits granted by that purchase are immediately revoked from your wallet.</li>
        <li>For fraudulent orders, no refund is issued and your account may be flagged for additional moderation review on future orders.</li>
        <li>The cascade is logged with an audit trail (actor admin, timestamp, reason) for compliance purposes.</li>
      </UL>

      <H2 id="8">8. Chargebacks</H2>
      <P>Please contact us first before initiating a chargeback. Initiating a chargeback without first giving us 7 days to resolve the dispute may result in your account being suspended pending investigation. Costs incurred by Optivize defending an unjustified chargeback may be deducted from any future refund.</P>

      <H2 id="9">9. Goodwill Credits</H2>
      <P>Where a full cash refund is not warranted under this policy, we may at our sole discretion issue a goodwill credit redeemable against future invoices. Goodwill credits are non-transferable, valid for 12 months, and cannot be exchanged for cash.</P>

      <H2 id="10">10. Modifications</H2>
      <P>We may amend this policy by posting an updated version. Refund requests are evaluated against the policy in force on the <em>date of order placement</em>, not the date the request is submitted.</P>

      <H2 id="11">11. Contact</H2>
      {CONTACT_BLOCK}
    </LegalLayout>
  );
}
